import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CreditCard,
  ArrowUpRight,
  ExternalLink,
  XCircle,
  Clock,
  Sparkles,
  Vault,
  Receipt,
  FileText,
  BarChart3,
} from "lucide-react";
import { useBillingStore } from "@/stores/billing/billing-store";
import { useVaultStore } from "@/stores/billing/vault-store";
import { VaultUsageMeter } from "@/components/billing/VaultUsageMeter";
import { ExtraCreditBalance } from "@/components/billing/ExtraCreditBalance";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";
import { VaultManager } from "@/components/billing/VaultManager";
import { TransactionHistory } from "@/components/billing/TransactionHistory";
import { BillingHistory } from "@/components/billing/BillingHistory";
import { UsageDashboard } from "@/components/billing/UsageDashboard";
import { api } from "@/services/api/client";
import { TIER_DISPLAY, CREDIT_PACKS } from "@/types/billing";
import type { RegistrationSelectedPlan } from "@/types";
import type {
  BillingInterval,
  Subscription,
  SubscriptionStatus,
  SubscriptionTier,
} from "@/types/billing";
import { isStripeManagedSubscription } from "@/types/billing";
import {
  parsePendingRegistrationSelectedPlan,
  readPendingRegistrationSelectedPlan,
  savePendingRegistrationSelectedPlan,
} from "@/utils/pendingRegistrationSelectedPlan";

/**
 * BillingPage
 * Protected page showing current subscription, vault usage, and billing actions.
 * Organized into tabs: Overview, Vaults, Transactions, Invoices, Usage.
 */

const STATUS_BADGES: Record<SubscriptionStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active: { label: "Active", variant: "default" },
  trialing: { label: "Trial", variant: "secondary" },
  past_due: { label: "Past Due", variant: "destructive" },
  canceled: { label: "Canceled", variant: "destructive" },
  suspended: { label: "Suspended", variant: "destructive" },
  unpaid: { label: "Unpaid", variant: "destructive" },
};

interface RegistrationContinuationResponse {
  hasIntent: boolean;
  action?: unknown;
  selectedPlan?: {
    tier?: unknown;
    billingInterval?: unknown;
  };
}

interface RegistrationCheckoutIntent {
  tier?: SubscriptionTier;
  billingInterval?: BillingInterval;
}

interface RegistrationCheckoutResponse {
  url: string;
}

const STRIPE_CHECKOUT_HOST = "checkout.stripe.com";
const TRIAL_FALLBACK_CONFLICT_MESSAGE =
  "Your trial did not start because your Stripe checkout is still active or being finalized. Use Resume checkout to continue your paid signup.";
const CHECKOUT_RESUME_IN_PROGRESS_MESSAGE =
  "Your checkout session is still being prepared. Try Resume checkout again in a moment.";
const CHECKOUT_RESUME_PROVISIONING_MESSAGE =
  "Your checkout already completed and is still being finalized. Stay on Billing and refresh in a moment.";

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function getDaysRemaining(dateStr: string): number {
  try {
    const end = new Date(dateStr);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  } catch {
    return 0;
  }
}

function isSubscriptionTier(value: unknown): value is SubscriptionTier {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(TIER_DISPLAY, value)
  );
}

function isBillingInterval(value: unknown): value is BillingInterval {
  return value === "monthly" || value === "annual";
}

function getRegistrationCheckoutIntent(
  continuation: RegistrationContinuationResponse | null,
  fallbackSelectedPlan: RegistrationSelectedPlan | undefined,
): RegistrationCheckoutIntent | null {
  if (!continuation?.hasIntent || continuation.action !== "checkout") {
    return null;
  }

  const fallbackTier = fallbackSelectedPlan?.tier;
  const fallbackBillingInterval =
    fallbackSelectedPlan && fallbackSelectedPlan.tier !== "free"
      ? fallbackSelectedPlan.billingInterval
      : undefined;

  const tier = continuation.selectedPlan?.tier ?? fallbackTier;
  const billingInterval =
    continuation.selectedPlan?.billingInterval ?? fallbackBillingInterval;

  return {
    ...(isSubscriptionTier(tier) ? { tier } : {}),
    ...(isBillingInterval(billingInterval) ? { billingInterval } : {}),
  };
}

function getRegistrationPlanLabel(intent: RegistrationCheckoutIntent): string {
  if (!intent.tier) {
    return "your selected plan";
  }

  const intervalLabel = intent.billingInterval
    ? ` ${intent.billingInterval}`
    : "";

  return `${TIER_DISPLAY[intent.tier].name}${intervalLabel}`;
}

function redirectToRegistrationCheckout(rawUrl: string): void {
  const url = new URL(rawUrl, window.location.origin);
  const isStripeCheckout =
    url.protocol === "https:" && url.hostname === STRIPE_CHECKOUT_HOST;
  const isSameOrigin = url.origin === window.location.origin;

  if (!isStripeCheckout && !isSameOrigin) {
    throw new Error("Unexpected billing redirect URL");
  }

  window.location.assign(url.toString());
}

function getErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  if (error !== null && typeof error === "object") {
    const message = Reflect.get(error, "message");
    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }

  return null;
}

function getErrorStatus(error: unknown): number | null {
  if (error === null || typeof error !== "object") {
    return null;
  }

  const status = Reflect.get(error, "status");
  return typeof status === "number" ? status : null;
}

function getRegistrationContinuationErrorMessage(
  action: "checkout" | "trial",
  error: unknown,
): string {
  const message = getErrorMessage(error);
  const status = getErrorStatus(error);
  const normalizedMessage = message?.toLowerCase() ?? "";

  if (action === "trial") {
    if (status === 409) {
      return TRIAL_FALLBACK_CONFLICT_MESSAGE;
    }

    if (message) {
      return `Your trial did not start. ${message}`;
    }

    return "Your trial did not start. Resume checkout to continue your paid signup, or try again in a moment.";
  }

  if (status === 409) {
    if (normalizedMessage.includes("provision")) {
      return CHECKOUT_RESUME_PROVISIONING_MESSAGE;
    }

    if (normalizedMessage.includes("progress")) {
      return CHECKOUT_RESUME_IN_PROGRESS_MESSAGE;
    }
  }

  return message ?? "Failed to resume checkout";
}

export const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    subscription,
    isLoading: billingLoading,
    error: billingError,
    fetchSubscription,
    createPortalSession,
    cancelSubscription,
  } = useBillingStore();

  const {
    myUsage,
    creditBalance,
    isLoading: vaultLoading,
    fetchMyUsage,
    fetchCreditBalance,
    purchaseCredits,
  } = useVaultStore();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [selectedCreditPack, setSelectedCreditPack] = useState<string | null>(null);
  const [registrationContinuation, setRegistrationContinuation] =
    useState<RegistrationContinuationResponse | null>(null);
  const [registrationContinuationLoading, setRegistrationContinuationLoading] =
    useState(false);
  const [registrationContinuationError, setRegistrationContinuationError] =
    useState<string | null>(null);
  const [registrationContinuationAction, setRegistrationContinuationAction] =
    useState<"checkout" | "trial" | null>(null);

  const fetchRegistrationContinuation = useCallback(async () => {
    setRegistrationContinuationLoading(true);
    setRegistrationContinuationError(null);

    try {
      const response = await api.get<RegistrationContinuationResponse>(
        "/billing/registration-continuation",
      );
      const selectedTier =
        typeof response.data?.selectedPlan?.tier === "string"
          ? response.data.selectedPlan.tier
          : null;
      const selectedBillingInterval =
        typeof response.data?.selectedPlan?.billingInterval === "string"
          ? response.data.selectedPlan.billingInterval
          : null;
      const responseSelectedPlan = parsePendingRegistrationSelectedPlan(
        selectedTier,
        selectedBillingInterval,
      );

      if (responseSelectedPlan) {
        savePendingRegistrationSelectedPlan(responseSelectedPlan);
      }
      setRegistrationContinuation(response.data ?? null);
    } catch (error) {
      setRegistrationContinuationError(
        error instanceof Error
          ? error.message
          : "Failed to load pending registration checkout",
      );
    } finally {
      setRegistrationContinuationLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
    fetchMyUsage();
    fetchCreditBalance();
  }, [fetchSubscription, fetchMyUsage, fetchCreditBalance]);

  useEffect(() => {
    fetchRegistrationContinuation();
  }, [fetchRegistrationContinuation]);

  const isLoading = billingLoading || vaultLoading;
  const tier = subscription?.tier || "free";
  const tierDisplay = TIER_DISPLAY[tier];
  const statusBadge = subscription ? STATUS_BADGES[subscription.status] : null;
  const hasStripeCustomerId =
    typeof subscription?.stripeCustomerId === "string" &&
    subscription.stripeCustomerId.trim().length > 0;
  const hasStripeSubscriptionId =
    typeof subscription?.stripeSubscriptionId === "string" &&
    subscription.stripeSubscriptionId.trim().length > 0;
  const isStripeManaged = isStripeManagedSubscription(subscription);
  const canManageStripeBilling = isStripeManaged && hasStripeCustomerId;
  const canCancelStripeSubscription = isStripeManaged && hasStripeSubscriptionId;
  const persistedSelectedPlan = readPendingRegistrationSelectedPlan();
  const registrationCheckoutIntent =
    getRegistrationCheckoutIntent(
      registrationContinuation,
      persistedSelectedPlan,
    );

  // Determine if we should show upgrade prompts
  const highUsageVaults = myUsage.filter((v) => v.usagePercent >= 80);
  const exhaustedVaults = myUsage.filter((v) => v.usagePercent >= 100);

  // Trial info
  const isTrialing = subscription?.status === "trialing";
  const trialDaysLeft = subscription?.trialEndsAt
    ? getDaysRemaining(subscription.trialEndsAt)
    : 0;

  const handlePurchaseCredits = async (packId: string) => {
    setSelectedCreditPack(packId);
    try {
      await purchaseCredits(packId);
    } catch {
      // Error handled in store
    } finally {
      setSelectedCreditPack(null);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelSubscription();
      setShowCancelConfirm(false);
    } catch {
      // Error handled in store
    }
  };

  const handleResumeRegistrationCheckout = async () => {
    setRegistrationContinuationAction("checkout");
    setRegistrationContinuationError(null);

    try {
      const checkout = await api.post<
        RegistrationCheckoutResponse,
        { successUrl: string; cancelUrl: string }
      >("/billing/registration-continuation/checkout", {
        successUrl: `${window.location.origin}/billing?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/billing?registrationCheckout=cancelled`,
      });

      if (!checkout.data?.url) {
        throw new Error("Missing checkout URL");
      }

      redirectToRegistrationCheckout(checkout.data.url);
    } catch (error) {
      setRegistrationContinuationError(
        getRegistrationContinuationErrorMessage("checkout", error),
      );
      setRegistrationContinuationAction(null);
    }
  };

  const handleStartRegistrationTrial = async () => {
    setRegistrationContinuationAction("trial");
    setRegistrationContinuationError(null);

    try {
      const response = await api.post<Subscription>(
        "/billing/registration-continuation/trial",
        {},
      );

      if (!response.data) {
        throw new Error("Missing trial subscription");
      }

      setRegistrationContinuation({ hasIntent: false });
      await Promise.all([
        fetchSubscription(),
        fetchRegistrationContinuation(),
      ]);
    } catch (error) {
      setRegistrationContinuationError(
        getRegistrationContinuationErrorMessage("trial", error),
      );
    } finally {
      setRegistrationContinuationAction(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Billing</h1>
        <p className="mt-1 text-muted-foreground">
          Manage your subscription, usage, and credits.
        </p>
      </div>

      {/* Error display */}
      {billingError && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/30 dark:text-red-300">
          {billingError}
        </div>
      )}

      {registrationContinuationError && (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/30 dark:text-red-300"
        >
          {registrationContinuationError}
        </div>
      )}

      {registrationCheckoutIntent && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-4 dark:border-amber-700 dark:bg-amber-950/30">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                Finish your {getRegistrationPlanLabel(registrationCheckoutIntent)} signup
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Checkout was not completed. Resume checkout to finish your paid signup, or try to start a trial instead and pick a paid plan later.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="gap-2"
                onClick={handleResumeRegistrationCheckout}
                disabled={
                  registrationContinuationLoading ||
                  registrationContinuationAction !== null
                }
              >
                <CreditCard className="h-4 w-4" />
                {registrationContinuationAction === "checkout"
                  ? "Opening..."
                  : "Resume checkout"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                onClick={handleStartRegistrationTrial}
                disabled={
                  registrationContinuationLoading ||
                  registrationContinuationAction !== null
                }
              >
                <Clock className="h-4 w-4" />
                {registrationContinuationAction === "trial"
                  ? "Starting..."
                  : "Start trial instead"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade prompts */}
      {exhaustedVaults.length > 0 && (
        <UpgradePrompt
          type="budget-exhausted"
          currentTier={tier}
          onAddCredits={() => document.getElementById("credits-section")?.scrollIntoView({ behavior: "smooth" })}
        />
      )}
      {exhaustedVaults.length === 0 && highUsageVaults.length > 0 && (
        <UpgradePrompt
          type="budget-warning"
          currentTier={tier}
          onAddCredits={() => document.getElementById("credits-section")?.scrollIntoView({ behavior: "smooth" })}
        />
      )}

      {/* Trial banner */}
      {isTrialing && (
        <div className="flex items-center gap-3 rounded-lg border border-purple-300 bg-purple-50 px-4 py-3 dark:border-purple-700 dark:bg-purple-950/30">
          <Clock className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
              Trial - {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} remaining
            </p>
            <p className="text-xs text-purple-700 dark:text-purple-300">
              {subscription?.trialEndsAt && `Ends ${formatDate(subscription.trialEndsAt)}`}
            </p>
          </div>
          <Button size="sm" onClick={() => navigate("/pricing")}>
            Upgrade Now
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" className="gap-1.5">
            <CreditCard className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="vaults" className="gap-1.5">
            <Vault className="h-4 w-4" />
            Vaults
          </TabsTrigger>
          <TabsTrigger value="transactions" className="gap-1.5">
            <Receipt className="h-4 w-4" />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="invoices" className="gap-1.5">
            <FileText className="h-4 w-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="usage" className="gap-1.5">
            <BarChart3 className="h-4 w-4" />
            Usage
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Current Plan Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5" />
                    Current Plan
                  </CardTitle>
                  <CardDescription>
                    {tierDisplay.description}
                  </CardDescription>
                </div>
                {statusBadge && (
                  <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                )}
                {subscription?.billingProvider === "internal" ? (
                  <Badge variant="outline">Internal plan</Badge>
                ) : null}
              </div>
            </CardHeader>
            <CardContent>
              {isLoading && !subscription ? (
                <div className="space-y-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ) : subscription ? (
                <div className="space-y-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-foreground">
                      {tierDisplay.name}
                    </span>
                    <Badge variant="outline" className="capitalize">
                      {subscription.billingInterval}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <span className="text-muted-foreground">Current period ends:</span>{" "}
                      <span className="font-medium text-foreground">
                        {formatDate(subscription.currentPeriodEnd)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Members:</span>{" "}
                      <span className="font-medium text-foreground">
                        {subscription.maxMembers === -1 ? "Unlimited" : subscription.maxMembers}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Vaults:</span>{" "}
                      <span className="font-medium text-foreground">
                        {subscription.maxVaults === -1 ? "Unlimited" : subscription.maxVaults}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Admins:</span>{" "}
                      <span className="font-medium text-foreground">
                        {subscription.maxAdmins === -1 ? "Unlimited" : subscription.maxAdmins}
                      </span>
                    </div>
                  </div>

                  {subscription.cancelAtPeriodEnd && (
                    <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-200">
                      <XCircle className="h-4 w-4" />
                      Cancels at end of billing period ({formatDate(subscription.currentPeriodEnd)})
                    </div>
                  )}

                  <Separator />

                  {/* Quick actions */}
                  <div className="flex flex-wrap gap-3">
                    {tier !== "enterprise" && (
                      <Button
                        variant="default"
                        onClick={() => navigate("/pricing")}
                        className="gap-2"
                      >
                        <ArrowUpRight className="h-4 w-4" />
                        {tier === "free" ? "Upgrade" : "Change Plan"}
                      </Button>
                    )}
                    {tier !== "free" && canManageStripeBilling && (
                      <Button
                        variant="outline"
                        onClick={() => createPortalSession()}
                        disabled={isLoading}
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Manage via Stripe
                      </Button>
                    )}
                    {tier !== "free" && canCancelStripeSubscription && !subscription.cancelAtPeriodEnd && (
                      <>
                        {showCancelConfirm ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Are you sure?</span>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleCancel}
                              disabled={isLoading}
                            >
                              Yes, Cancel
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowCancelConfirm(false)}
                            >
                              No
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setShowCancelConfirm(true)}
                          >
                            Cancel Subscription
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                /* No subscription / Free tier */
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="text-lg font-semibold text-foreground">Free Plan</p>
                      <p className="text-sm text-muted-foreground">
                        You are on the free plan. Upgrade to unlock more features.
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => navigate("/pricing")} className="gap-2">
                    <ArrowUpRight className="h-4 w-4" />
                    View Plans & Upgrade
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage Overview */}
          <Card>
            <CardHeader>
              <CardTitle>Usage Overview</CardTitle>
              <CardDescription>
                AI budget usage across your token vaults.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading && myUsage.length === 0 ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : myUsage.length > 0 ? (
                <div className="space-y-4">
                  {myUsage.map((usage) => (
                    <VaultUsageMeter key={usage.vaultId} vault={usage} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No vault usage data available.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Extra Credits */}
          <div id="credits-section">
            <Card>
              <CardHeader>
                <CardTitle>Extra Credits</CardTitle>
                <CardDescription>
                  Purchase additional credits that never expire. Used after your plan budget is depleted.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {creditBalance && (
                  <ExtraCreditBalance
                    balanceCents={creditBalance.extraBalanceCents}
                    onAddCredits={() => {
                      // Scroll to credit packs
                    }}
                  />
                )}

                <Separator />

                <div>
                  <h3 className="mb-3 text-sm font-semibold text-foreground">
                    Credit Packs
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {CREDIT_PACKS.map((pack) => (
                      <Button
                        key={pack.id}
                        variant="outline"
                        className="flex h-auto flex-col gap-1 py-4"
                        onClick={() => handlePurchaseCredits(pack.id)}
                        disabled={isLoading || selectedCreditPack === pack.id}
                      >
                        <span className="text-lg font-bold">{pack.label}</span>
                        <span className="text-xs text-muted-foreground">
                          ${(pack.creditValue / 100).toFixed(0)} in credits
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vaults Tab */}
        <TabsContent value="vaults">
          <VaultManager />
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <TransactionHistory />
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <BillingHistory />
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage">
          <UsageDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
};
